/**
 * Lead capture data contract constants (Lead Flow M2).
 * Aligns with docs/lead-flow-current-state-audit.md and existing CRM lead types.
 */

import { AUTOMAPA_LEGAL_VERSION_DEFAULTS } from "../automap/automapLegalVersionDefaults";

/** @readonly */
export const LEAD_CONTEXT_TYPES = Object.freeze({
  VEHICLE_LISTING: "vehicle_listing",
  DEALER_PROFILE: "dealer_profile",
  DEALER_DIRECTORY: "dealer_directory",
  SEARCH_RESULT: "search_result",
  GENERAL_DEALER: "general_dealer",
  UNKNOWN: "unknown",
});

/** Canonical submission lead types (M2 contract). */
export const LEAD_TYPES = Object.freeze({
  VEHICLE_INQUIRY: "vehicle_inquiry",
  DEALER_INQUIRY: "dealer_inquiry",
  GENERAL_INQUIRY: "general_inquiry",
});

/**
 * Maps M2 contract lead types to existing Client View CRM `leadType` values.
 * @see server/utils/clientViewLeadValidation.js CLIENT_VIEW_LEAD_TYPES
 */
export const CRM_LEAD_TYPE_MAP = Object.freeze({
  [LEAD_TYPES.VEHICLE_INQUIRY]: "vehicle_inquiry",
  [LEAD_TYPES.DEALER_INQUIRY]: "dealer_inquiry",
  [LEAD_TYPES.GENERAL_INQUIRY]: "general",
});

export const LEAD_PREFERRED_CONTACT_METHODS = Object.freeze({
  WHATSAPP: "whatsapp",
  PHONE: "phone",
  EMAIL: "email",
  ANY: "any",
});

export const LEAD_SUBMISSION_STATUSES = Object.freeze({
  SUCCESS: "success",
  VALIDATION_ERROR: "validation_error",
  DUPLICATE: "duplicate",
  RATE_LIMITED: "rate_limited",
  SPAM_REJECTED: "spam_rejected",
  SERVICE_ERROR: "service_error",
  NETWORK_ERROR: "network_error",
  UNKNOWN_ERROR: "unknown_error",
});

export const LEAD_SOURCE_PAGES = Object.freeze({
  VEHICLE_DETAIL: "vehicle_detail",
  DEALER_PROFILE: "dealer_profile",
  DEALER_DIRECTORY: "dealer_directory",
  SEARCH_RESULTS: "search_results",
});

export const LEAD_SOURCE_COMPONENTS = Object.freeze({
  VEHICLE_CONTACT_PANEL: "vehicle_detail_contact_panel",
  VEHICLE_DETAIL_MOBILE: "vehicle_detail_mobile",
  VEHICLE_DETAIL_FORM: "vehicle_detail_form",
  DEALER_PROFILE_HERO: "dealer_profile_hero",
  DEALER_PROFILE_MOBILE: "dealer_profile_mobile_cta",
  DEALER_INVENTORY_CARD: "dealer_inventory_card",
  DEALER_DIRECTORY_CARD: "dealer_directory_card",
  DEALER_DIRECTORY_FEATURED: "dealer_directory_featured",
  SEARCH_RESULT_CARD: "vehicle_search_result_card",
  SAME_DEALER_RAIL: "vehicle_recommendation_same_dealer",
});

/** Default Spanish buyer messages (autofill when message left blank). */
export const LEAD_DEFAULT_MESSAGES_ES = Object.freeze({
  [LEAD_CONTEXT_TYPES.VEHICLE_LISTING]:
    "Hola, estoy interesado en este vehículo publicado en AutoMapa. ¿Sigue disponible?",
  [LEAD_CONTEXT_TYPES.SEARCH_RESULT]:
    "Hola, estoy interesado en este vehículo publicado en AutoMapa. ¿Sigue disponible?",
  [LEAD_CONTEXT_TYPES.DEALER_PROFILE]:
    "Hola, encontré tu concesionario en AutoMapa. Me gustaría recibir más información sobre vehículos disponibles.",
  [LEAD_CONTEXT_TYPES.DEALER_DIRECTORY]:
    "Hola, encontré tu concesionario en AutoMapa. Me gustaría recibir más información.",
  [LEAD_CONTEXT_TYPES.GENERAL_DEALER]:
    "Hola, me gustaría recibir más información.",
  [LEAD_CONTEXT_TYPES.UNKNOWN]: "Hola, me gustaría recibir más información.",
});

export const LEAD_MODAL_TITLES_ES = Object.freeze({
  [LEAD_CONTEXT_TYPES.VEHICLE_LISTING]: "Contactar al vendedor",
  [LEAD_CONTEXT_TYPES.SEARCH_RESULT]: "Contactar al vendedor",
  [LEAD_CONTEXT_TYPES.DEALER_PROFILE]: "Contactar concesionario",
  [LEAD_CONTEXT_TYPES.DEALER_DIRECTORY]: "Contactar concesionario",
  [LEAD_CONTEXT_TYPES.GENERAL_DEALER]: "Solicitar información",
  [LEAD_CONTEXT_TYPES.UNKNOWN]: "Solicitar información",
});

export const LEAD_MODAL_INTROS_ES = Object.freeze({
  [LEAD_CONTEXT_TYPES.VEHICLE_LISTING]:
    "Envía tus datos para consultar disponibilidad, precio y condiciones.",
  [LEAD_CONTEXT_TYPES.SEARCH_RESULT]:
    "Envía tus datos para consultar disponibilidad, precio y condiciones.",
  [LEAD_CONTEXT_TYPES.DEALER_PROFILE]:
    "Envía tus datos para que el concesionario pueda responderte.",
  [LEAD_CONTEXT_TYPES.DEALER_DIRECTORY]:
    "Envía tus datos para que el concesionario pueda responderte.",
  [LEAD_CONTEXT_TYPES.GENERAL_DEALER]: "Déjanos tus datos para recibir más información.",
  [LEAD_CONTEXT_TYPES.UNKNOWN]: "Déjanos tus datos para recibir más información.",
});

export const LEAD_SUBMIT_LABELS_ES = Object.freeze({
  default: "Enviar solicitud",
  vehicle: "Solicitar información",
  dealer: "Contactar concesionario",
});

/** Buyer-safe Spanish result messages. */
export const LEAD_RESULT_MESSAGES_ES = Object.freeze({
  [LEAD_SUBMISSION_STATUSES.SUCCESS]:
    "Gracias. Recibimos tu solicitud y la enviaremos al concesionario.",
  [LEAD_SUBMISSION_STATUSES.VALIDATION_ERROR]: "Revisa los datos e intenta nuevamente.",
  [LEAD_SUBMISSION_STATUSES.DUPLICATE]:
    "Ya recibimos una solicitud similar. Espera un momento antes de enviar otra.",
  [LEAD_SUBMISSION_STATUSES.RATE_LIMITED]:
    "Has enviado varias solicitudes en poco tiempo. Intenta nuevamente más tarde.",
  [LEAD_SUBMISSION_STATUSES.SPAM_REJECTED]:
    "No pudimos enviar tu solicitud. Revisa la información e intenta nuevamente.",
  [LEAD_SUBMISSION_STATUSES.SERVICE_ERROR]: "No pudimos enviar tu solicitud. Intenta nuevamente.",
  [LEAD_SUBMISSION_STATUSES.NETWORK_ERROR]:
    "No pudimos conectar. Revisa tu conexión e intenta nuevamente.",
  [LEAD_SUBMISSION_STATUSES.UNKNOWN_ERROR]: "No pudimos enviar tu solicitud. Intenta nuevamente.",
});

export const LEAD_PRIVACY_DEFAULTS = Object.freeze({
  requiresConsent: true,
  privacyPolicyHref: "/privacidad",
  termsHref: "/terminos",
  privacyPolicyVersion: AUTOMAPA_LEGAL_VERSION_DEFAULTS.PRIVACY_POLICY,
  allowAnalytics: true,
  piiBoundary: "lead_api_only",
});

/** Keys stripped from dealer/listing inputs when building safe context. */
export const LEAD_STRIPPED_DEALER_KEYS = Object.freeze([
  "whatsapp",
  "phone",
  "email",
  "mainWhatsapp",
  "mainPhone",
  "mainEmail",
  "address",
  "raw",
  "rawSource",
  "businessName",
  "sellerPhone",
  "dealerWhatsapp",
  "dealerPhone",
  "dealerEmail",
]);

export const LEAD_STRIPPED_LISTING_KEYS = Object.freeze([
  "raw",
  "dealerWhatsapp",
  "sellerPhone",
  "dealerPhone",
  "dealerEmail",
  "sellerEmail",
]);

export const LEAD_ANALYTICS_BLOCKED_KEYS = Object.freeze([
  "buyerName",
  "buyerPhone",
  "buyerEmail",
  "buyerWhatsapp",
  "message",
  "phone",
  "whatsapp",
  "email",
  "firstName",
  "lastName",
  "contact",
  "messageToDealer",
  "dealerPhone",
  "dealerWhatsapp",
  "dealerEmail",
  "dealerAddress",
  "rawDealer",
  "rawListing",
  "raw",
  "rawSource",
  "rawPayload",
  "rawResponse",
  "rawApiPayload",
  "rawApiResponse",
  "honeypot",
  "submissionToken",
  "clientSubmissionId",
  "duplicateHash",
  "consentText",
  "consentAcceptedText",
  "internalMessage",
  "stackTrace",
  "stack",
  "errors",
  "formErrors",
  "currentUrl",
  "landingPath",
  "gclid",
  "fbclid",
  "ipAddress",
  "ip",
]);
